/* eslint-disable @typescript-eslint/ban-ts-comment */
import axios from 'axios';
import { Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { OrderSavedSearch, ShipmentSavedSearch } from '@/model';

export interface UseSavedSearchesResponse<T> {
  createSavedSearch: (search: T) => Promise<T | undefined>;
  deleteSavedSearch: (searchId: number) => Promise<void>;
  loadPopularSearches: () => Promise<void>;
  loadSavedSearches: () => Promise<void>;
  popularSearches: Ref<Loadable<T[]>>;
  savedSearches: Ref<Loadable<T[]>>;
  updateSavedSearch: (search: T) => Promise<T | undefined>;
}

type UseOrderSavedSearchesResponse = UseSavedSearchesResponse<OrderSavedSearch>;
type UseShipmentSavedSearchesResponse =
  UseSavedSearchesResponse<ShipmentSavedSearch>;

export function useSavedSearches(type: 'Order'): UseOrderSavedSearchesResponse;
export function useSavedSearches(
  type: 'Shipment',
): UseShipmentSavedSearchesResponse;
export function useSavedSearches(
  type: 'Order' | 'Shipment',
): UseOrderSavedSearchesResponse | UseShipmentSavedSearchesResponse {
  const { t } = useI18n();
  const toast = useToast();
  const orderSavedSearches: Ref<Loadable<OrderSavedSearch[]>> = ref({
    loading: false,
    data: [],
  });
  const shipmentSavedSearches: Ref<Loadable<ShipmentSavedSearch[]>> = ref({
    loading: false,
    data: [],
  });
  const orderPopularSearches: Ref<Loadable<OrderSavedSearch[]>> = ref({
    loading: false,
    data: [],
  });
  const shipmentPopularSearches: Ref<Loadable<ShipmentSavedSearch[]>> = ref({
    loading: false,
    data: [],
  });

  const createSavedSearch = async (
    search: OrderSavedSearch | ShipmentSavedSearch,
  ) => {
    const savedSearches =
      type === 'Order' ? orderSavedSearches : shipmentSavedSearches;
    const create =
      type === 'Order'
        ? api.orderSavedSearch.create.bind(api.orderSavedSearch)
        : api.shipmentSavedSearch.create.bind(api.shipmentSavedSearch);
    try {
      savedSearches.value.loading = true;
      // @ts-ignore
      const savedSearch = await create(search);
      // @ts-ignore
      savedSearches.value.data?.push(savedSearch);

      if (savedSearch.defaultSearch && savedSearches.value.data) {
        const previousDefault = savedSearches.value.data.find(
          (s) => s.defaultSearch && s.id !== savedSearch.id,
        );
        if (previousDefault) {
          previousDefault.defaultSearch = false;
        }
      }

      return savedSearch;
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      savedSearches.value.error = e;
      console.error('Error saving user searches', e);
      toast.addError({
        detail: t('errors.savingParam', {
          param: t('search.searches'),
        }),
        error: e,
      });
    } finally {
      savedSearches.value.loading = false;
    }
  };

  const loadPopularSearches = async () => {
    const popularSearches =
      type === 'Order' ? orderPopularSearches : shipmentPopularSearches;
    const load =
      type === 'Order'
        ? api.orderSavedSearch.findAllGlobal.bind(api.orderSavedSearch)
        : api.shipmentSavedSearch.findAllGlobal.bind(api.shipmentSavedSearch);
    try {
      popularSearches.value.loading = true;
      popularSearches.value.data = await load();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      popularSearches.value.error = e;
      console.error('Error loading popular searches', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('search.searches'),
        }),
        error: e,
      });
    } finally {
      popularSearches.value.loading = false;
    }
  };

  const loadSavedSearches = async () => {
    const savedSearches =
      type === 'Order' ? orderSavedSearches : shipmentSavedSearches;
    const load =
      type === 'Order'
        ? api.orderSavedSearch.findAllForUser.bind(api.orderSavedSearch)
        : api.shipmentSavedSearch.findAllForUser.bind(api.shipmentSavedSearch);
    try {
      savedSearches.value.loading = true;
      savedSearches.value.data = await load();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      savedSearches.value.error = e;
      console.error('Error loading user searches', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('search.searches'),
        }),
        error: e,
      });
    } finally {
      savedSearches.value.loading = false;
    }
  };

  const updateSavedSearch = async (
    search: OrderSavedSearch | ShipmentSavedSearch,
  ) => {
    const savedSearches =
      type === 'Order' ? orderSavedSearches : shipmentSavedSearches;
    const update =
      type === 'Order'
        ? api.orderSavedSearch.update.bind(api.orderSavedSearch)
        : api.shipmentSavedSearch.update.bind(api.shipmentSavedSearch);
    try {
      savedSearches.value.loading = true;
      // @ts-ignore
      const savedSearch = await update(search);
      const index = savedSearches.value.data?.findIndex(
        (s) => s.id === savedSearch.id,
      );
      if (index !== undefined) {
        // @ts-ignore
        savedSearches.value.data?.splice(index, 1, savedSearch);
      }

      if (savedSearch.defaultSearch && savedSearches.value.data) {
        const previousDefault = savedSearches.value.data.find(
          (s) => s.defaultSearch && s.id !== savedSearch.id,
        );
        if (previousDefault) {
          previousDefault.defaultSearch = false;
        }
      }

      return savedSearch;
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      savedSearches.value.error = e;
      console.error('Error saving user searches', e);
      toast.addError({
        detail: t('errors.savingParam', {
          param: t('search.searches'),
        }),
        error: e,
      });
    } finally {
      savedSearches.value.loading = false;
    }
  };

  const deleteSavedSearch = async (searchId: number): Promise<void> => {
    const savedSearches =
      type === 'Order' ? orderSavedSearches : shipmentSavedSearches;
    const deleteSearch =
      type === 'Order'
        ? api.orderSavedSearch.delete.bind(api.orderSavedSearch)
        : api.shipmentSavedSearch.delete.bind(api.shipmentSavedSearch);
    try {
      savedSearches.value.loading = true;
      deleteSearch(searchId);
      if (type === 'Order') {
        orderSavedSearches.value.data = orderSavedSearches.value.data?.filter(
          (s) => s.id !== searchId,
        );
      } else {
        shipmentSavedSearches.value.data =
          shipmentSavedSearches.value.data?.filter((s) => s.id !== searchId);
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      savedSearches.value.error = e;
      console.error(`Error deleting user search ${searchId}`, e);
      toast.addError({
        detail: t('errors.deletingParam', {
          param: t('search.searches'),
        }),
        error: e,
      });
    } finally {
      savedSearches.value.loading = false;
    }
  };

  return {
    // @ts-ignore
    createSavedSearch,
    deleteSavedSearch,
    loadPopularSearches,
    loadSavedSearches,
    popularSearches:
      type === 'Order' ? orderPopularSearches : shipmentPopularSearches,
    savedSearches:
      type === 'Order' ? orderSavedSearches : shipmentSavedSearches,
    // @ts-ignore
    updateSavedSearch,
  };
}
